import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Flex, Group, Menu } from "@mantine/core";

import { IconSelector } from "@tabler/icons-react";

import { ORGANIZATION_ID } from "@/constants/local-storage";
import { NavbarContext } from "@/contexts/navbar";
import { OrganizationsContext } from "@/contexts/organizations";
import { useActiveOrganization } from "@/hooks/useActiveOrganization";
import { getInitials } from "@/utils/str";

import { Loading } from "../../components/Loading";
import { TextWrapper } from "../../components/TextWrapper";
import { NAVBAR_ITEM_EXPANDED_WIDTH } from "../constants/navbar";
import {
  PRACTICE_SWITCH_COLLAPSED_HEIGHT,
  PRACTICE_SWITCH_EXPANDED_HEIGHT,
} from "../constants/practice-switch";
import { PracticeOption } from "../types/practice-switch";
import { getPracticeAddress } from "../utils/practice-switch";

import NavBarItem from "./NavBarItem";

export default function PracticeSwitch() {
  const { collapsed } = useContext(NavbarContext);
  const { organizations } = useContext(OrganizationsContext);
  const activeOrganization = useActiveOrganization();
  const [practices, setPractices] = useState<PracticeOption[]>([]);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setPractices(
      organizations.map((organization) => ({
        id: organization.id!,
        name: organization.name ?? "Unknown",
        initials: getInitials(organization.name),
        address: getPracticeAddress(organization.address?.[0]),
      })),
    );
  }, [organizations]);

  if (practices.length === 0) {
    return <Loading />;
  }

  const handleSwitch = (practiceId: string) => {
    const previousPracticeId = localStorage.getItem(ORGANIZATION_ID);
    localStorage.setItem(ORGANIZATION_ID, practiceId);

    if (previousPracticeId && location.pathname.includes(previousPracticeId)) {
      navigate(location.pathname.replace(previousPracticeId, practiceId));
      window.location.reload();
      return;
    }

    navigate("/clients");
    window.location.reload();
  };

  const activePractice =
    practices.find((practice) => practice.id === activeOrganization?.id) ?? practices[0];

  const hasMultiplePractices = practices.length > 1;

  return (
    <NavBarItem
      collapsed={collapsed}
      label="Practice switch"
      h={collapsed ? PRACTICE_SWITCH_COLLAPSED_HEIGHT : "auto"}
      mih={collapsed ? undefined : PRACTICE_SWITCH_EXPANDED_HEIGHT}
    >
      <Menu
        withArrow
        shadow="sm"
        disabled={!hasMultiplePractices}
        width={NAVBAR_ITEM_EXPANDED_WIDTH}
        position={collapsed ? "right-start" : "bottom"}
      >
        <Menu.Target>
          <Group
            gap={2}
            w="100%"
            px={collapsed ? 0 : 4}
            py={collapsed ? 0 : "xs"}
            data-testid="practice-switch-target"
            justify={collapsed ? "center" : undefined}
            aria-label={collapsed && hasMultiplePractices ? "Select practice" : undefined}
            style={{ userSelect: "none", cursor: hasMultiplePractices ? "pointer" : "default" }}
          >
            {collapsed ? (
              <TextWrapper variant="bold" c="burgundy.9">
                {activePractice.initials}
              </TextWrapper>
            ) : (
              <>
                <Flex w="100%" align="center" justify="space-between">
                  <TextWrapper variant="bold" c="burgundy.9" data-testid="practice-name">
                    {activePractice.name}
                  </TextWrapper>
                  {hasMultiplePractices && (
                    <IconSelector size={20} color="black" aria-label="Select practice" />
                  )}
                </Flex>
                <TextWrapper w="100%" c="dark.3">
                  {activePractice.address}
                </TextWrapper>
              </>
            )}
          </Group>
        </Menu.Target>
        <Menu.Dropdown>
          {practices.map((practice) => (
            <Menu.Item
              key={practice.id}
              onClick={() => handleSwitch(practice.id)}
              disabled={practice.id === activePractice.id}
            >
              <TextWrapper>{practice.name}</TextWrapper>
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
    </NavBarItem>
  );
}
